import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import api from '../utils/api';
import CarModal from '../components/CarModal';
import { pushCtaHover } from '../utils/dataLayer';

const Home = () => {
  const { language, isRTL } = useLanguage();
  const t = translations[language];
  const [cars, setCars] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCar, setSelectedCar] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [carsRes, destinationsRes, reviewsRes] = await Promise.all([
          api.get('/cars'),
          api.get('/destinations'),
          api.get('/reviews')
        ]);
        setCars(carsRes.data.slice(0, 3));
        setDestinations(destinationsRes.data.slice(0, 4));
        setReviews(reviewsRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: t.feature1Title,
      description: t.feature1Desc,
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      title: t.feature2Title,
      description: t.feature2Desc,
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: t.feature3Title,
      description: t.feature3Desc,
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: t.feature4Title,
      description: t.feature4Desc,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-blackC via-transparent to-blackC z-10" />
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1631295868223-63265b40d9e4?q=80&w=2070')] bg-cover bg-center opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-blackC via-transparent to-blackC z-10" />
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-1/4 left-10 w-px h-32 bg-gradient-to-b from-transparent via-primary to-transparent opacity-50" />
        <div className="absolute bottom-1/4 right-10 w-px h-32 bg-gradient-to-b from-transparent via-primary to-transparent opacity-50" />

        {/* Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="space-y-8">
            <p className="text-primary text-sm tracking-[0.3em] uppercase animate-fade-in-up opacity-0 animation-delay-100">
              {t.heroSubtitle}
            </p>

            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-cream leading-tight animate-fade-in-up opacity-0 animation-delay-200">
              {t.heroTitle}
            </h1>

            <p className="max-w-2xl mx-auto text-gray-silver text-lg md:text-xl leading-relaxed animate-fade-in-up opacity-0 animation-delay-300">
              {t.heroDescription}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4 animate-fade-in-up opacity-0 animation-delay-400">
              <Link to="/book" onMouseEnter={() => pushCtaHover('hero_book_now')} className="btn-primary">
                <span>{t.bookNow}</span>
                <svg className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link to="/cars" className="btn-secondary">
                {t.exploreFleet}
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
          <div className="w-6 h-10 border border-primary/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-primary rounded-full mt-2 animate-bounce" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 lg:py-32 bg-blackC relative">
        <div className="absolute inset-0 grain-overlay" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <p className="section-subtitle">{t.featuresSubtitle}</p>
            <h2 className="section-title">{t.featuresTitle}</h2>
            <div className="divider-gold mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-8 bg-dark border border-gray-dark hover:border-primary/30 transition-all duration-500"
              >
                <div className="w-16 h-16 rounded-full bg-charcoal flex items-center justify-center text-primary mb-6 group-hover:bg-primary group-hover:text-blackC transition-all duration-500">
                  {feature.icon}
                </div>
                <h3 className="font-display text-xl text-cream mb-3">{feature.title}</h3>
                <p className="text-gray-silver text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet Section */}
      <section className="py-24 lg:py-32 bg-dark relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-16 gap-6">
            <div>
              <p className="section-subtitle">{t.carsSubtitle}</p>
              <h2 className="section-title">{t.viewCars}</h2>
              <div className="divider-gold mt-6" />
            </div>
            <Link to="/cars" className="btn-secondary">
              {t.viewAll}
              <svg className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cars.map((car, index) => (
                <div
                  key={car._id}
                  className="group card-luxury cursor-pointer"
                  style={{ animationDelay: `${index * 100}ms` }}
                  onClick={() => setSelectedCar(car)}
                >
                  <div className="relative overflow-hidden">
                    {car.images && car.images.length > 0 ? (
                      <img
                        src={car.images[0].url}
                        alt={car.name}
                        className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-64 bg-charcoal flex items-center justify-center">
                        <svg className="w-16 h-16 text-gray" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
                        </svg>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-blackC to-transparent opacity-60" />
                    <div className="absolute top-4 right-4 bg-primary text-blackC px-3 py-1 text-xs font-medium tracking-wide uppercase">
                      {car.type}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-2xl text-cream mb-4">{car.name}</h3>
                    <div className="flex items-center gap-6 text-gray-silver text-sm mb-6">
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {car.passengerCapacity} {t.passengers}
                      </span>
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {car.luggageCapacity} {t.bags}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      {car.pricingType === 'fixed' && car.fixedPrice && (
                        <span className="text-primary font-display text-2xl">{car.fixedPrice} EGP</span>
                      )}
                      {car.pricingType === 'perKm' && car.pricePerKm && (
                        <span className="text-primary font-display text-xl">{car.pricePerKm} EGP/km</span>
                      )}
                      <Link
                        to="/book"
                        onMouseEnter={() => pushCtaHover('fleet_book')}
                        className="text-primary text-sm font-medium tracking-wide uppercase hover:text-tertiary transition-colors flex items-center gap-2"
                      >
                        {t.book}
                        <svg className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Destinations Section */}
      <section className="py-24 lg:py-32 bg-blackC relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="section-subtitle">{t.destinationsSubtitle}</p>
            <h2 className="section-title">{t.popularDestinations}</h2>
            <div className="divider-gold mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {destinations.map((dest, index) => (
              <Link
                key={dest._id}
                to="/book"
                className="group relative h-48 overflow-hidden"
              >
                {dest.image?.url ? (
                  <>
                    <img
                      src={dest.image.url}
                      alt={language === 'en' ? dest.name : dest.nameAr}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-blackC via-blackC/60 to-transparent" />
                  </>
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-charcoal to-blackC" />
                )}
                <div className="absolute inset-0 border border-gray-dark group-hover:border-primary/50 transition-colors duration-500" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end z-10">
                  <h3 className="font-display text-2xl text-cream mb-2">
                    {language === 'en' ? dest.name : dest.nameAr}
                  </h3>
                  {dest.pricingType === 'fixed' && dest.fixedPrice && (
                    <p className="text-primary text-sm">
                      {t.startingFrom} <span className="font-display text-lg">{dest.fixedPrice} EGP</span>
                    </p>
                  )}
                  {dest.distance && (
                    <p className="text-gray-light text-sm mt-1">
                      {t.distance}: {dest.distance} km
                    </p>
                  )}
                </div>
                <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary opacity-0 group-hover:opacity-100 transform translate-x-4 group-hover:translate-x-0 transition-all duration-500 z-10">
                  <svg className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24 lg:py-32 bg-dark relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="section-subtitle">{t.reviewsSubtitle}</p>
            <h2 className="section-title">{t.customerReviews}</h2>
            <div className="divider-gold mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <div
                key={review._id}
                className="p-8 bg-blackC border border-gray-dark"
              >
                <div className="flex items-center gap-1 mb-6 text-primary">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className={`w-5 h-5 ${i < review.rating ? 'fill-current' : 'stroke-current fill-transparent'}`}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  ))}
                </div>
                {review.comment && (
                  <p className="text-gray-silver leading-relaxed mb-6 italic">"{review.comment}"</p>
                )}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-dark flex items-center justify-center text-primary font-display">
                    {review.customerName?.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-cream font-medium">{review.customerName}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-blackC relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-cream mb-6">
            {t.ctaTitle}
          </h2>
          <p className="text-gray-silver text-lg mb-10 max-w-2xl mx-auto">
            {t.ctaDescription}
          </p>
          <Link to="/book" onMouseEnter={() => pushCtaHover('cta_section_book')} className="btn-primary">
            <span>{t.bookNow}</span>
            <svg className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </section>

      {/* Car Details Modal */}
      {selectedCar && (
        <CarModal car={selectedCar} onClose={() => setSelectedCar(null)} />
      )}
    </div>
  );
};

export default Home;